import React from 'react';

interface ArgumentStep {
  label: string;
  text: string;
}

interface ArgumentBriefProps {
  thesis: string;
  keyArguments: ArgumentStep[];
  personaColor: string;
  onReadFull: () => void;
}

const STEP_STYLES: Record<string, { dotBg: string; dotBorder: string; labelColor: string }> = {
  concedes: { dotBg: '#f59e0b', dotBorder: '#f59e0b', labelColor: '#d97706' },
  'concedes to the left': { dotBg: '#f59e0b', dotBorder: '#f59e0b', labelColor: '#d97706' },
  'concedes to the right': { dotBg: '#f59e0b', dotBorder: '#f59e0b', labelColor: '#d97706' }
};

const getStepStyle = (label: string, personaColor: string) => {
  const key = label.toLowerCase();
  if (key === 'concludes') {
    return { dotBg: personaColor, dotBorder: personaColor, labelColor: personaColor };
  }
  return STEP_STYLES[key] || { dotBg: '#cbd5e1', dotBorder: '#e2e8f0', labelColor: '#94a3b8' };
};

const ArgumentBrief: React.FC<ArgumentBriefProps> = ({
  thesis,
  keyArguments,
  personaColor,
  onReadFull
}) => {
  return (
    <div>
      {/* Thesis card */}
      <div
        className="rounded-xl p-5 mb-6"
        style={{
          borderLeft: `4px solid ${personaColor}`,
          background: `${personaColor}08`
        }}
      >
        <p
          className="text-[10px] font-bold tracking-wider mb-2"
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            textTransform: 'uppercase',
            color: personaColor
          }}
        >
          THESIS
        </p>
        <p
          className="text-[17px] font-medium text-slate-900 leading-relaxed"
          style={{ fontFamily: "'Source Serif 4', Georgia, serif", lineHeight: '1.7' }}
        >
          {thesis}
        </p>
      </div>

      {/* Argument timeline */}
      <div className="pl-1 mb-6">
        {keyArguments.map((step, i) => {
          const style = getStepStyle(step.label, personaColor);
          const isLast = i === keyArguments.length - 1;

          return (
            <div key={i} className="flex gap-4" style={{ paddingBottom: isLast ? 0 : '18px' }}>
              {/* Dot column */}
              <div className="flex flex-col items-center w-5 shrink-0">
                <div
                  className="w-[10px] h-[10px] rounded-full border-2 shrink-0"
                  style={{ background: style.dotBg, borderColor: style.dotBorder }}
                />
                {!isLast && <div className="w-[2px] flex-1 bg-[#e2e8f0] mt-1" />}
              </div>

              {/* Content column */}
              <div className="flex-1 min-w-0 -mt-0.5">
                <p
                  className="text-[11px] font-bold tracking-wider mb-1"
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: style.labelColor
                  }}
                >
                  {step.label}
                </p>
                <p
                  className="text-[15px] text-[#334155] leading-relaxed"
                  style={{ fontFamily: "'Source Serif 4', Georgia, serif", lineHeight: '1.65' }}
                >
                  {step.text}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Read full CTA */}
      <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-[10px] p-4 flex items-center justify-between gap-4">
        <p
          className="text-[14px] text-[#64748b]"
          style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
        >
          This is the argument in brief. The full analysis includes evidence, context, and a
          comprehension check.
        </p>
        <button
          onClick={onReadFull}
          className="text-[13px] font-bold text-[#059669] hover:text-[#047857] transition-colors whitespace-nowrap"
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
        >
          Read full →
        </button>
      </div>
    </div>
  );
};

export default ArgumentBrief;
